import { Component, Inject, OnInit, ViewChild, ElementRef } from "@angular/core";
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from "@angular/material/legacy-dialog";
import { UntypedFormBuilder, Validators, UntypedFormGroup, ValidationErrors } from "@angular/forms";
import { first, map, startWith, debounceTime, switchMap, distinctUntilChanged } from "rxjs/operators";
import { MatLegacyInput as MatInput } from "@angular/material/legacy-input";
import { DatePipe } from "@angular/common";
import { MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig } from "@angular/material/legacy-dialog";
import { Subscription, Observable } from "rxjs";
import * as _ from "lodash";
import { MatLegacyAutocomplete as MatAutocomplete } from "@angular/material/legacy-autocomplete";
import { MatLegacyTabChangeEvent as MatTabChangeEvent } from "@angular/material/legacy-tabs";
import * as moment from "moment";
import { Router } from "@angular/router";

import { ProfileImageDialogComponent } from "./profileImage";
import { UserService, StorageService, AlertService, MediaWatcherService, LookupsService, GoogleAnalyticsService } from "../_services";
import { IUserDetails } from "../_models";
import { IUser, ICountry } from "../_models";
import { AppGlobals } from "../app.globals";
import { IdentityDocumentImagesDialogComponent } from "./identityDocumentImages";
import { IdentityDocumentHelpDialogComponent } from "./identityDocumentHelp";
import { ProfileImageHelpDialogComponent } from "./profileImageHelp";
import { UnSavedChangesDialogComponent } from "../_components/unSavedChangesDialog/unSavedChangesDialog.component";
import { dateGreaterThenValidator } from "../_helpers/forms/date.validator";

@Component({
    selector: "profile-dialog",
    templateUrl: "profileDialog.component.html",
    styleUrls: ["profileDialog.component.css"],
    providers: [DatePipe]
})
export class ProfileDialogComponent implements OnInit {
    accountDetailsForm: UntypedFormGroup;
    imageSrc: string;
    defaultEmail: string;
    id: number;
    genderTypeId: number;
    currentUser: IUser;
    studentEmergencyContacts: any[] = [];
    primaryEmergencyContact: any;
    secondaryEmergencyContact: any;
    fullName: string = null;
    isMobile = false;
    mediaWatcherSubscription: Subscription;
    identityDocumentImages = [];
    identityDocumentImagesChanged = false;
    studentIdentityDocumentData = null;
    identityDocumentRejectedReason = null;
    needsStudentReviewForIdentityDocumentInformation = false;
    needsAdminReviewForIdentityDocumentInformation = false;
    originalIdentityDocumentImages = [];
    originalIdentityDocumentRejectedReason = null;
    studentCode = null;
    allowEdit = false;
    selectedTabIndex = 0;
    rowversion = null;
    identityDocumentInfoRequired = false;
    profilePictureChanged = false;
    profilePictureRejectedReason = null;
    missingIdentityDocumentBirthCountry = false;

    countries: ICountry[];
    states = [];
    filteredEmergencyContact1States = [];
    filteredEmergencyContact2States = [];
    genderTypes = [];
    educationLevels = [];
    contactTypes = [];
    primaryContactRowversion = null;
    primaryContactId = 0;
    primaryContactBbUniqueId = null;
    secondaryContactRowversion = null;
    secondaryContactId = 0;
    secondaryContactBbUniqueId = null;

    identityDocumentDetailsRowversion = null;
    identityDocumentDetailsId = 0;
    identityDocumentTypes = [];
    italianMunicipalities = [];
    filteredItalianMunicipalities: Observable<any[]>;
    nationalities = [];
    birthCountries = [];

    daysBeforePermitToStayExpiryForRenewal = 60;

    @ViewChild("ssn") ssnInput: MatInput;
    @ViewChild("auto") matAutocomplete: MatAutocomplete;

    constructor(private dialogRef: MatDialogRef<ProfileDialogComponent>, @Inject(MAT_DIALOG_DATA) data: any, private formBuilder: UntypedFormBuilder, private userService: UserService, private lookupsService: LookupsService,
        private datePipe: DatePipe, public dialog: MatDialog, private appGlobals: AppGlobals, private storageService: StorageService, private alertService: AlertService, private googleAnalyticsService: GoogleAnalyticsService,
        private mediaWatcherService: MediaWatcherService, private router: Router) {
        this.imageSrc = data.currentUser.imageSrc;
        this.defaultEmail = data.currentUser.defaultEmail;
        this.id = data.currentUser.id;

        this.currentUser = data.currentUser;
        this.allowEdit = this.currentUser.hasValidOrientation;
    }

    ngOnInit() {
        this.appGlobals.pageTitleValue = "Profile";

        //this.googleAnalyticsService.logPageView("/profile");

        this.mediaWatcherSubscription = this.mediaWatcherService.isTabletLarge.subscribe(isMobile => {
            this.isMobile = isMobile;
        });

        //genderTypes
        this.lookupsService.getGenderTypes()
            .pipe(first())
            .subscribe((response: any) => {
                this.genderTypes = _.filter(response, f => f.description !== "Both") as any;
            });

        //application education levels
        this.lookupsService.getEducationLevels()
            .pipe(first())
            .subscribe((response: any) => {
                this.educationLevels = _.orderBy(response, "description") as any;
            });

        //emergency contact types
        this.lookupsService.getStudentEmergencyContactTypes()
            .pipe(first())
            .subscribe((response: any) => {
                this.contactTypes = _.orderBy(response, "description") as any;
            });

        this.lookupsService.getIdentityDocumentTypes()
            .pipe(first())
            .subscribe((identityDocumentTypes: any) => {
                this.identityDocumentTypes = identityDocumentTypes;
            });

        this.lookupsService.getSpecificSystemDefaults(this.appGlobals.systemDefaults.daysBeforePermitToStayExpiryForRenewal)
            .pipe(first())
            .subscribe((response: any) => {
                if (response && response.length > 0) {
                    this.daysBeforePermitToStayExpiryForRenewal = response[0].numberColumn;
                }
            });

        this.initializeForm();

        //countries
        this.lookupsService.getCountries()
            .pipe(first())
            .subscribe((countries: any) => {
                this.countries = _.orderBy(countries, "description") as any;
                this.birthCountries = _.orderBy(countries, "description");
                this.nationalities = _.orderBy(countries, "description");

                //states
                this.lookupsService.getStates()
                    .pipe(first())
                    .subscribe((response: any) => {
                        this.states = _.orderBy(response, "description") as any;;
                    });

                this.onChanges();

                this.userService.getStudentDetails(this.id)
                    .pipe(first())
                    .subscribe((result: IUserDetails) => {
                        if (result) {
                            this.fullName = `${this.currentUser.firstName}${this.currentUser.preferredName ? `&nbsp;(${this.currentUser.preferredName})` : ""}${result.middleName ? `&nbsp;${result.middleName}` : ""} ${this.currentUser.lastName}${result.studentCode ? `<b>&nbsp;(${result.studentCode})</b>` : ""}`;
                            this.genderTypeId = result.genderTypeId;
                            this.studentEmergencyContacts = result.studentEmergencyContacts;
                            this.studentIdentityDocumentData = result.studentIdentityDocumentData;
                            this.studentCode = result.studentCode;
                            this.profilePictureRejectedReason = result.attachmentRejectedReason;
                            this.rowversion = result.rowversion;

                            this.accountDetailsForm.get("gender").setValue(result.gender);
                            this.accountDetailsForm.get("currency").setValue(result.currencyDescription);
                            this.accountDetailsForm.get("ssn").setValue(result.ssn);
                            this.accountDetailsForm.get("dateOfBirth").setValue(this.datePipe.transform(result.dateOfBirth, "MMM dd, yyyy"));
                            this.accountDetailsForm.get("applicationType").setValue(result.applicationType);
                            this.accountDetailsForm.get("provider").setValue(result.providerDescription);
                            this.accountDetailsForm.get("school").setValue(result.schoolDescription);
                            this.accountDetailsForm.get("birthCity").setValue(result.birthCity);
                            this.accountDetailsForm.get("birthState").setValue(result.birthState);
                            this.accountDetailsForm.get("birthCountry").setValue(result.birthCountry);
                            this.accountDetailsForm.get("language").setValue(result.languageDescription);
                            this.accountDetailsForm.get("primaryCitizenship").setValue(result.primaryCitizenship);
                            this.accountDetailsForm.get("secondaryCitizenship").setValue(result.secondaryCitizenship);
                            this.accountDetailsForm.get("address").setValue(result.fullAddress ? result.fullAddress.replace(/[\n\r]+/g, ", ") : "");
                            this.accountDetailsForm.get("phoneNo").setValue(result.defaultPhoneNumber);
                            this.accountDetailsForm.get("email").setValue(result.primaryEmailAddress);
                            this.accountDetailsForm.get("additionalPhoneNos").setValue(result.phoneNumbers);
                            this.accountDetailsForm.get("additionalEmails").setValue(result.emailAddresses);

                            //family information
                            if (result.studentEmergencyContacts && result.studentEmergencyContacts.length > 0) {
                                this.primaryEmergencyContact = _.find(result.studentEmergencyContacts, ["isPrimaryContact", true]) as any;

                                this.secondaryEmergencyContact = _.find(result.studentEmergencyContacts, ["isPrimaryContact", false]) as any;

                                if (this.primaryEmergencyContact) {
                                    this.primaryContactRowversion = this.primaryEmergencyContact.rowversion;
                                    this.primaryContactId = this.primaryEmergencyContact.id;
                                    this.primaryContactBbUniqueId = this.primaryEmergencyContact.bbUniqueId;

                                    this.accountDetailsForm.get("primaryEmergencyContactFirstName").setValue(this.primaryEmergencyContact.firstName);
                                    this.accountDetailsForm.get("primaryEmergencyContactLastName").setValue(this.primaryEmergencyContact.lastName);
                                    this.accountDetailsForm.get("primaryEmergencyContactTypeId").setValue(this.primaryEmergencyContact.studentEmergencyContactTypeId);
                                    this.accountDetailsForm.get("primaryEmergencyContactGenderTypeId").setValue(this.primaryEmergencyContact.genderTypeId);
                                    this.accountDetailsForm.get("primaryEmergencyContactBirthCountryId").setValue(this.primaryEmergencyContact.birthCountryId);
                                    this.accountDetailsForm.get("primaryEmergencyContactAddress").setValue(this.primaryEmergencyContact.address);
                                    this.accountDetailsForm.get("primaryEmergencyContactCity").setValue(this.primaryEmergencyContact.city);
                                    this.accountDetailsForm.get("primaryEmergencyContactPostCode").setValue(this.primaryEmergencyContact.postCode);
                                    this.accountDetailsForm.get("primaryEmergencyContactPhoneNo").setValue(this.primaryEmergencyContact.phoneNo);
                                    this.accountDetailsForm.get("primaryEmergencyContactCountryId").setValue(this.primaryEmergencyContact.countryId);
                                    this.accountDetailsForm.get("primaryEmergencyContactStateId").setValue(this.primaryEmergencyContact.stateId);
                                    this.accountDetailsForm.get("primaryEmergencyContactEmail").setValue(this.primaryEmergencyContact.email);
                                    this.accountDetailsForm.get("primaryEmergencyContactEducationLevelId").setValue(this.primaryEmergencyContact.educationLevelId);
                                    this.accountDetailsForm.get("primaryEmergencyContactOccupation").setValue(this.primaryEmergencyContact.occupation);
                                    this.accountDetailsForm.get("primaryEmergencyContactEmployer").setValue(this.primaryEmergencyContact.employer);
                                    this.accountDetailsForm.get("isPrimaryEmergencyContact").setValue(this.primaryEmergencyContact.isEmergencyContact);
                                }

                                if (this.secondaryEmergencyContact) {
                                    this.secondaryContactRowversion = this.secondaryEmergencyContact.rowversion;
                                    this.secondaryContactId = this.secondaryEmergencyContact.id;
                                    this.secondaryContactBbUniqueId = this.secondaryEmergencyContact.bbUniqueId;

                                    this.accountDetailsForm.get("secondaryEmergencyContactFirstName").setValue(this.secondaryEmergencyContact.firstName);
                                    this.accountDetailsForm.get("secondaryEmergencyContactLastName").setValue(this.secondaryEmergencyContact.lastName);
                                    this.accountDetailsForm.get("secondaryEmergencyContactTypeId").setValue(this.secondaryEmergencyContact.studentEmergencyContactTypeId);
                                    this.accountDetailsForm.get("secondaryEmergencyContactGenderTypeId").setValue(this.secondaryEmergencyContact.genderTypeId);
                                    this.accountDetailsForm.get("secondaryEmergencyContactBirthCountryId").setValue(this.secondaryEmergencyContact.birthCountryId);
                                    this.accountDetailsForm.get("secondaryEmergencyContactAddress").setValue(this.secondaryEmergencyContact.address);
                                    this.accountDetailsForm.get("secondaryEmergencyContactCity").setValue(this.secondaryEmergencyContact.city);
                                    this.accountDetailsForm.get("secondaryEmergencyContactPostCode").setValue(this.secondaryEmergencyContact.postCode);
                                    this.accountDetailsForm.get("secondaryEmergencyContactPhoneNo").setValue(this.secondaryEmergencyContact.phoneNo);
                                    this.accountDetailsForm.get("secondaryEmergencyContactCountryId").setValue(this.secondaryEmergencyContact.countryId);
                                    this.accountDetailsForm.get("secondaryEmergencyContactStateId").setValue(this.secondaryEmergencyContact.stateId);
                                    this.accountDetailsForm.get("secondaryEmergencyContactEmail").setValue(this.secondaryEmergencyContact.email);
                                    this.accountDetailsForm.get("secondaryEmergencyContactEducationLevelId").setValue(this.secondaryEmergencyContact.educationLevelId);
                                    this.accountDetailsForm.get("secondaryEmergencyContactOccupation").setValue(this.secondaryEmergencyContact.occupation);
                                    this.accountDetailsForm.get("secondaryEmergencyContactEmployer").setValue(this.secondaryEmergencyContact.employer);
                                    this.accountDetailsForm.get("isSecondaryEmergencyContact").setValue(this.secondaryEmergencyContact.isEmergencyContact);
                                }
                            }

                            //identity document information
                            if (result.studentIdentityDocumentData) {
                                this.accountDetailsForm.get("identityDocumentNumber").setValue(result.studentIdentityDocumentData.documentNumber, { emitEvent: false });
                                this.accountDetailsForm.get("identityDocumentTypeId").setValue(result.studentIdentityDocumentData.identityDocumentTypeId, { emitEvent: false });
                                this.accountDetailsForm.get("identityDocumentFirstName").setValue(result.studentIdentityDocumentData.firstName);
                                this.accountDetailsForm.get("identityDocumentMiddleName").setValue(result.studentIdentityDocumentData.middleName);
                                this.accountDetailsForm.get("identityDocumentLastName").setValue(result.studentIdentityDocumentData.lastName);
                                this.accountDetailsForm.get("identityDocumentDateOfBirth").setValue(result.studentIdentityDocumentData.dateOfBirth);
                                this.accountDetailsForm.get("identityDocumentGenderTypeId").setValue(result.studentIdentityDocumentData.genderTypeId);
                                this.accountDetailsForm.get("identityDocumentCountryId").setValue(result.studentIdentityDocumentData.issuingCountryId, { emitEvent: false });
                                this.accountDetailsForm.get("identityDocumentNationalityId").setValue(result.studentIdentityDocumentData.nationalityId, { emitEvent: false });
                                this.accountDetailsForm.get("identityDocumentBirthCountryId").setValue(result.studentIdentityDocumentData.birthCountryId, { emitEvent: false });
                                this.accountDetailsForm.get("dateOfIssue").setValue(result.studentIdentityDocumentData.issueDate, { emitEvent: false });
                                this.accountDetailsForm.get("expiryDate").setValue(result.studentIdentityDocumentData.expirationDate, { emitEvent: false });

                                if (result.studentIdentityDocumentData.birthMunicipalityId) {
                                    if (this.allowEdit) {
                                        this.accountDetailsForm.get("identityDocumentBirthCommuneId").enable({ emitEvent: false });
                                        this.accountDetailsForm.get("identityDocumentBirthCommuneId").setValidators([Validators.required]);
                                        this.accountDetailsForm.get("identityDocumentBirthCommuneId").reset({ emitEvent: false });
                                    }

                                    var birthMunicipality = {
                                        id: result.studentIdentityDocumentData.birthMunicipalityId,
                                        description: result.studentIdentityDocumentData.birthMunicipalityDescription
                                    }
                                    this.italianMunicipalities.push(birthMunicipality);
                                    this.accountDetailsForm.get("identityDocumentBirthCommuneId").setValue(result.studentIdentityDocumentData.birthMunicipalityId, { emitEvent: false });
                                }

                                this.identityDocumentImages = result.studentIdentityDocumentData.identityDocumentImages;
                                this.originalIdentityDocumentImages = result.studentIdentityDocumentData.identityDocumentImages;
                                this.identityDocumentDetailsId = result.studentIdentityDocumentData.id;
                                this.identityDocumentDetailsRowversion = result.studentIdentityDocumentData.rowversion;
                                this.identityDocumentRejectedReason = result.studentIdentityDocumentData.rejectedReason;
                                this.originalIdentityDocumentRejectedReason = result.studentIdentityDocumentData.rejectedReason;
                                this.needsStudentReviewForIdentityDocumentInformation = !!result.studentIdentityDocumentData.identityDocumentImages.find(f => f.needsStudentReview);
                                this.needsAdminReviewForIdentityDocumentInformation = result.studentIdentityDocumentData.needsAdminReview;
                            } else {
                                this.accountDetailsForm.get("identityDocumentFirstName").setValue(result.firstName);
                                this.accountDetailsForm.get("identityDocumentMiddleName").setValue(result.middleName);
                                this.accountDetailsForm.get("identityDocumentLastName").setValue(result.lastName);
                                this.accountDetailsForm.get("identityDocumentDateOfBirth").setValue(result.dateOfBirth);
                                this.accountDetailsForm.get("identityDocumentGenderTypeId").setValue(result.genderTypeId);
                                this.accountDetailsForm.get("identityDocumentBirthCountryId").setValue(result.birthCountryId, { emitEvent: false });

                                if (result.birthCountryId === 11 && this.allowEdit) {
                                    this.accountDetailsForm.get("identityDocumentBirthCommuneId").enable({ emitEvent: false });
                                    // this.accountDetailsForm.get("identityDocumentBirthCommuneId").setValidators([Validators.required]);
                                    this.accountDetailsForm.get("identityDocumentBirthCommuneId").reset(null, { emitEvent: false });
                                }
                            }

                            if (this.allowEdit && !result.studentIdentityDocumentData?.birthCountryId && !result.birthCountryId) {
                                this.missingIdentityDocumentBirthCountry = true;

                                this.accountDetailsForm.get("identityDocumentBirthCountryId").enable({ emitEvent: false });
                                this.accountDetailsForm.get("identityDocumentBirthCountryId").reset(null, { emitEvent: false });
                            }
                        }
                    });
            });
    }

    initializeForm() {
        this.accountDetailsForm = this.formBuilder.group({
            gender: [{ value: "", disabled: true }],
            currency: [{ value: "", disabled: true }],
            ssn: [{ value: null, disabled: false }, Validators.pattern(/^(?!000)(?!666)(?!9)\d{3}[- ]?(?!00)\d{2}[- ]?(?!0000)\d{4}$/)],
            dateOfBirth: [{ value: "", disabled: true }],
            applicationType: [{ value: "", disabled: true }],
            secondaryCitizenship: [{ value: "", disabled: true }],
            provider: [{ value: "", disabled: true }],
            school: [{ value: "", disabled: true }],
            birthCity: [{ value: "", disabled: true }],
            birthState: [{ value: "", disabled: true }],
            birthCountry: [{ value: "", disabled: true }],
            language: [{ value: "", disabled: true }],
            primaryCitizenship: [{ value: "", disabled: true }],
            address: [{ value: "", disabled: true }],
            email: [{ value: "", disabled: true }],
            phoneNo: [{ value: "", disabled: true }],
            additionalEmails: [{ value: "", disabled: true }],
            additionalPhoneNos: [{ value: "", disabled: true }],

            primaryEmergencyContactFirstName: [{ value: "", disabled: !this.allowEdit }, [Validators.required, Validators.pattern(/^([\x20-\x7E])+$/)]],
            primaryEmergencyContactLastName: [{ value: "", disabled: !this.allowEdit }, [Validators.required, Validators.pattern(/^([\x20-\x7E])+$/)]],
            primaryEmergencyContactGenderTypeId: [{ value: "", disabled: !this.allowEdit }, Validators.required],
            primaryEmergencyContactTypeId: [{ value: "", disabled: !this.allowEdit }, Validators.required],
            primaryEmergencyContactBirthCountryId: [{ value: "", disabled: !this.allowEdit }],
            primaryEmergencyContactAddress: [{ value: "", disabled: !this.allowEdit }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            primaryEmergencyContactCity: [{ value: "", disabled: !this.allowEdit }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            primaryEmergencyContactPostCode: [{ value: "", disabled: !this.allowEdit }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            primaryEmergencyContactCountryId: [{ value: "", disabled: !this.allowEdit }],
            primaryEmergencyContactStateId: [{ value: "", disabled: !this.allowEdit }],
            primaryEmergencyContactEmail: [{ value: "", disabled: !this.allowEdit }, [Validators.email, Validators.required]],
            primaryEmergencyContactPhoneNo: [{ value: "", disabled: !this.allowEdit }, [Validators.required, Validators.minLength(10), Validators.pattern(/^[+]{1}\d+$/)]],
            primaryEmergencyContactEducationLevelId: [{ value: "", disabled: !this.allowEdit }],
            primaryEmergencyContactOccupation: [{ value: "", disabled: !this.allowEdit }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            primaryEmergencyContactEmployer: [{ value: "", disabled: !this.allowEdit }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            isPrimaryEmergencyContact: [{ value: true, disabled: true }],

            secondaryEmergencyContactFirstName: [{ value: "", disabled: !this.allowEdit }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            secondaryEmergencyContactLastName: [{ value: "", disabled: true }, [Validators.required, Validators.pattern(/^([\x20-\x7E])+$/)]],
            secondaryEmergencyContactGenderTypeId: [{ value: "", disabled: true }, Validators.required],
            secondaryEmergencyContactTypeId: [{ value: "", disabled: true }, Validators.required],
            secondaryEmergencyContactBirthCountryId: [{ value: "", disabled: true }],
            secondaryEmergencyContactAddress: [{ value: "", disabled: true }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            secondaryEmergencyContactCity: [{ value: "", disabled: true }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            secondaryEmergencyContactPostCode: [{ value: "", disabled: true }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            secondaryEmergencyContactCountryId: [{ value: "", disabled: true }],
            secondaryEmergencyContactStateId: [{ value: "", disabled: true }],
            secondaryEmergencyContactEmail: [{ value: "", disabled: true }, [Validators.email, Validators.required]],
            secondaryEmergencyContactPhoneNo: [{ value: "", disabled: true }, [Validators.minLength(10), Validators.pattern(/^[+]{1}\d+$/), Validators.required]],
            secondaryEmergencyContactEducationLevelId: [{ value: "", disabled: true }],
            secondaryEmergencyContactOccupation: [{ value: "", disabled: true }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            secondaryEmergencyContactEmployer: [{ value: "", disabled: true }, [Validators.pattern(/^([\x20-\x7E])+$/)]],
            isSecondaryEmergencyContact: [{ value: "", disabled: true }],

            identityDocumentNumber: [{ value: null, disabled: !this.allowEdit }],
            identityDocumentTypeId: [{ value: null, disabled: !this.allowEdit }],
            identityDocumentFirstName: [{ value: null, disabled: true }],
            identityDocumentMiddleName: [{ value: null, disabled: true }],
            identityDocumentLastName: [{ value: null, disabled: true }],
            identityDocumentDateOfBirth: [{ value: null, disabled: true }],
            identityDocumentGenderTypeId: [{ value: null, disabled: true }],
            identityDocumentCountryId: [{ value: null, disabled: !this.allowEdit }],
            identityDocumentNationalityId: [{ value: null, disabled: !this.allowEdit }],
            identityDocumentBirthCountryId: [{ value: null, disabled: true }],
            identityDocumentBirthCommuneId: [{ value: null, disabled: true }],
            dateOfIssue: [{ value: null, disabled: !this.allowEdit }],
            expiryDate: [{ value: null, disabled: !this.allowEdit }, [dateGreaterThenValidator("dateOfIssue", true)]],
        });
    }

    onChanges() {
        this.accountDetailsForm.get("currency").valueChanges
            .subscribe(currency => {
                if (currency && currency === "US Dollar") {
                    this.profileFormControls.ssn.setValidators([Validators.required, Validators.pattern(/^(?!000)(?!666)(?!9)\d{3}[- ]?(?!00)\d{2}[- ]?(?!0000)\d{4}$/)]);

                    this.ssnInput.focus();
                } else {
                    this.profileFormControls.ssn.clearValidators();
                    this.profileFormControls.ssn.setValidators([Validators.pattern(/^(?!000)(?!666)(?!9)\d{3}[- ]?(?!00)\d{2}[- ]?(?!0000)\d{4}$/)]);
                }

                this.profileFormControls.ssn.updateValueAndValidity();
            });

        this.accountDetailsForm.get("ssn").valueChanges
            .pipe(
                debounceTime(500),
                distinctUntilChanged()
            )
            .subscribe(ssn => {
                if (ssn) {
                    const val = ssn.trim();

                    if (val && val.length === 9 && !val.match("^\\d{3}-\\d{2}-\\d{4}$") && val.indexOf("-") === -1)
                        this.accountDetailsForm.get("ssn").setValue(val.slice(0, 3) + "-" + val.slice(3, 5) + "-" + val.slice(5, 9));
                }
            });

        this.accountDetailsForm.get("primaryEmergencyContactCity").valueChanges
            .subscribe(city => {
                if (city && city.trim().length === 0) {
                    this.accountDetailsForm.get("primaryEmergencyContactCity").reset();
                }
            });

        this.accountDetailsForm.get("primaryEmergencyContactPostCode").valueChanges
            .subscribe(postCode => {
                if (postCode && postCode.trim().length === 0) {
                    this.accountDetailsForm.get("primaryEmergencyContactPostCode").reset();
                }
            });

        this.accountDetailsForm.get("primaryEmergencyContactLastName").valueChanges
            .subscribe(lastName => {
                if (lastName && lastName.trim().length === 0) {
                    this.accountDetailsForm.get("primaryEmergencyContactLastName").reset();
                }
            });

        this.accountDetailsForm.get("primaryEmergencyContactEmployer").valueChanges
            .subscribe(employer => {
                if (employer && employer.trim().length === 0) {
                    this.accountDetailsForm.get("primaryEmergencyContactEmployer").reset();
                }
            });

        this.accountDetailsForm.get("primaryEmergencyContactOccupation").valueChanges
            .subscribe(occupation => {
                if (occupation && occupation.trim().length === 0) {
                    this.accountDetailsForm.get("primaryEmergencyContactOccupation").reset();
                }
            });


        this.accountDetailsForm.get("primaryEmergencyContactBirthCountryId").valueChanges
            .subscribe(data => {
                if (!data || data === "0") {
                    this.accountDetailsForm.get("primaryEmergencyContactBirthCountryId").reset(null, { emitEvent: false });
                }
            });

        this.accountDetailsForm.get("primaryEmergencyContactEducationLevelId").valueChanges
            .subscribe(data => {
                if (!data || data === "0") {
                    this.accountDetailsForm.get("primaryEmergencyContactEducationLevelId").reset(null, { emitEvent: false });
                }
            });

        this.accountDetailsForm.get("secondaryEmergencyContactCity").valueChanges
            .subscribe(city => {
                if (city && city.trim().length === 0) {
                    this.accountDetailsForm.get("secondaryEmergencyContactCity").reset();
                }
            });

        this.accountDetailsForm.get("secondaryEmergencyContactPostCode").valueChanges
            .subscribe(postCode => {
                if (postCode && postCode.trim().length === 0) {
                    this.accountDetailsForm.get("secondaryEmergencyContactPostCode").reset();
                }
            });

        this.accountDetailsForm.get("secondaryEmergencyContactLastName").valueChanges
            .subscribe(lastName => {
                if (lastName && lastName.trim().length === 0) {
                    this.accountDetailsForm.get("secondaryEmergencyContactLastName").reset();
                }
            });

        this.accountDetailsForm.get("secondaryEmergencyContactEmployer").valueChanges
            .subscribe(employer => {
                if (employer && employer.trim().length === 0) {
                    this.accountDetailsForm.get("secondaryEmergencyContactEmployer").reset();
                }
            });

        this.accountDetailsForm.get("secondaryEmergencyContactOccupation").valueChanges
            .subscribe(occupation => {
                if (occupation && occupation.trim().length === 0) {
                    this.accountDetailsForm.get("secondaryEmergencyContactOccupation").reset();
                }
            });

        this.accountDetailsForm.get("secondaryEmergencyContactBirthCountryId").valueChanges
            .subscribe(data => {
                if (!data || data === "0") {
                    this.accountDetailsForm.get("secondaryEmergencyContactBirthCountryId").reset(null, { emitEvent: false });
                }
            });

        this.accountDetailsForm.get("secondaryEmergencyContactEducationLevelId").valueChanges
            .subscribe(data => {
                if (!data || data === "0") {
                    this.accountDetailsForm.get("secondaryEmergencyContactEducationLevelId").reset(null, { emitEvent: false });
                }
            });

        this.accountDetailsForm.get("primaryEmergencyContactCountryId").valueChanges
            .subscribe(selectedCountry => {
                if (!selectedCountry || selectedCountry === "0") {
                    this.accountDetailsForm.get("primaryEmergencyContactCountryId").setValue(null, { emitEvent: false });
                } else {
                    var phoneCountry = _.find(this.countries, { "id": selectedCountry }) as ICountry;
                    var currentPhoneDialingCode = _.find(this.countries, { "dialingCode": this.profileFormControls.primaryEmergencyContactPhoneNo.value }) as ICountry;

                    if (phoneCountry && (!this.profileFormControls.primaryEmergencyContactPhoneNo.value || currentPhoneDialingCode)) {
                        setTimeout(() => {
                            this.accountDetailsForm.get("primaryEmergencyContactPhoneNo").setValue(phoneCountry.dialingCode);
                        }, null);
                    }

                    this.filteredEmergencyContact1States = this.states.filter((f: any) => f.countryId === selectedCountry);
                }

                if ((selectedCountry === 1 || selectedCountry === 11) && this.allowEdit) {
                    this.accountDetailsForm.get("primaryEmergencyContactStateId").enable();
                } else {
                    this.accountDetailsForm.get("primaryEmergencyContactStateId").reset();
                    this.accountDetailsForm.get("primaryEmergencyContactStateId").disable();
                }
            });

        this.accountDetailsForm.get("secondaryEmergencyContactCountryId").valueChanges
            .subscribe(selectedCountry => {
                if (!selectedCountry || selectedCountry === "0") {
                    this.accountDetailsForm.get("secondaryEmergencyContactCountryId").setValue(null, { emitEvent: false });
                } else {
                    var phoneCountry = _.find(this.countries, { "id": selectedCountry }) as ICountry;
                    var currentPhoneDialingCode = _.find(this.countries, { "dialingCode": this.profileFormControls.secondaryEmergencyContactPhoneNo.value }) as ICountry;

                    if (phoneCountry && (!this.profileFormControls.secondaryEmergencyContactPhoneNo.value || currentPhoneDialingCode)) {
                        setTimeout(() => {
                            this.accountDetailsForm.get("secondaryEmergencyContactPhoneNo").setValue(phoneCountry.dialingCode);
                        }, null);
                    }

                    this.filteredEmergencyContact2States = this.states.filter((f: any) => f.countryId === selectedCountry);
                }

                if ((selectedCountry === 1 || selectedCountry === 11) && this.allowEdit) {
                    this.accountDetailsForm.get("secondaryEmergencyContactStateId").enable();
                } else {
                    this.accountDetailsForm.get("secondaryEmergencyContactStateId").reset();
                    this.accountDetailsForm.get("secondaryEmergencyContactStateId").disable();
                }
            });

        this.accountDetailsForm.get("primaryEmergencyContactFirstName").valueChanges
            .subscribe(firstName => {
                if (firstName && this.allowEdit) {
                    if (firstName.trim().length === 0) {
                        this.accountDetailsForm.get("primaryEmergencyContactFirstName").reset();
                    } else {
                        this.accountDetailsForm.get("primaryEmergencyContactLastName").enable();
                        this.accountDetailsForm.get("primaryEmergencyContactGenderTypeId").enable();
                        this.accountDetailsForm.get("primaryEmergencyContactTypeId").enable();
                        this.accountDetailsForm.get("primaryEmergencyContactBirthCountryId").enable();
                        this.accountDetailsForm.get("primaryEmergencyContactAddress").enable();
                        this.accountDetailsForm.get("primaryEmergencyContactCity").enable();
                        this.accountDetailsForm.get("primaryEmergencyContactPostCode").enable();
                        this.accountDetailsForm.get("primaryEmergencyContactCountryId").enable();
                        this.accountDetailsForm.get("primaryEmergencyContactEmail").enable();
                        this.accountDetailsForm.get("primaryEmergencyContactPhoneNo").enable();
                        this.accountDetailsForm.get("primaryEmergencyContactEducationLevelId").enable();
                        this.accountDetailsForm.get("primaryEmergencyContactOccupation").enable();
                        this.accountDetailsForm.get("primaryEmergencyContactEmployer").enable();
                        //this.accountDetailsForm.get("isPrimaryEmergencyContact").enable();
                    }
                } else {
                    this.accountDetailsForm.get("primaryEmergencyContactLastName").disable();
                    this.accountDetailsForm.get("primaryEmergencyContactGenderTypeId").disable();
                    this.accountDetailsForm.get("primaryEmergencyContactTypeId").disable();
                    this.accountDetailsForm.get("primaryEmergencyContactBirthCountryId").disable();
                    this.accountDetailsForm.get("primaryEmergencyContactAddress").disable();
                    this.accountDetailsForm.get("primaryEmergencyContactCity").disable();
                    this.accountDetailsForm.get("primaryEmergencyContactCountryId").disable();
                    this.accountDetailsForm.get("primaryEmergencyContactPostCode").disable();
                    this.accountDetailsForm.get("primaryEmergencyContactEmail").disable();
                    this.accountDetailsForm.get("primaryEmergencyContactPhoneNo").disable();
                    this.accountDetailsForm.get("primaryEmergencyContactEducationLevelId").disable();
                    this.accountDetailsForm.get("primaryEmergencyContactOccupation").disable();
                    this.accountDetailsForm.get("primaryEmergencyContactEmployer").disable();
                }

                if (!firstName && this.allowEdit) {
                    this.accountDetailsForm.get("primaryEmergencyContactLastName").reset();
                    this.accountDetailsForm.get("primaryEmergencyContactGenderTypeId").reset();
                    this.accountDetailsForm.get("primaryEmergencyContactTypeId").reset();
                    this.accountDetailsForm.get("primaryEmergencyContactBirthCountryId").reset();
                    this.accountDetailsForm.get("primaryEmergencyContactAddress").reset();
                    this.accountDetailsForm.get("primaryEmergencyContactCity").reset();
                    this.accountDetailsForm.get("primaryEmergencyContactPostCode").reset();
                    this.accountDetailsForm.get("primaryEmergencyContactCountryId").reset();
                    this.accountDetailsForm.get("primaryEmergencyContactEmail").reset();
                    this.accountDetailsForm.get("primaryEmergencyContactPhoneNo").reset();
                    this.accountDetailsForm.get("primaryEmergencyContactEducationLevelId").reset();
                    this.accountDetailsForm.get("primaryEmergencyContactOccupation").reset();
                    this.accountDetailsForm.get("primaryEmergencyContactEmployer").reset();
                }
            });

        this.accountDetailsForm.get("secondaryEmergencyContactFirstName").valueChanges
            .subscribe(firstName => {
                if (firstName && this.allowEdit) {
                    if (firstName.trim().length === 0) {
                        this.accountDetailsForm.get("secondaryEmergencyContactFirstName").reset();
                    } else {
                        this.accountDetailsForm.get("secondaryEmergencyContactLastName").enable();
                        this.accountDetailsForm.get("secondaryEmergencyContactGenderTypeId").enable();
                        this.accountDetailsForm.get("secondaryEmergencyContactTypeId").enable();
                        this.accountDetailsForm.get("secondaryEmergencyContactBirthCountryId").enable();
                        this.accountDetailsForm.get("secondaryEmergencyContactAddress").enable();
                        this.accountDetailsForm.get("secondaryEmergencyContactCity").enable();
                        this.accountDetailsForm.get("secondaryEmergencyContactPostCode").enable();
                        this.accountDetailsForm.get("secondaryEmergencyContactCountryId").enable();
                        this.accountDetailsForm.get("secondaryEmergencyContactEmail").enable();
                        this.accountDetailsForm.get("secondaryEmergencyContactPhoneNo").enable();
                        this.accountDetailsForm.get("secondaryEmergencyContactEducationLevelId").enable();
                        this.accountDetailsForm.get("secondaryEmergencyContactOccupation").enable();
                        this.accountDetailsForm.get("secondaryEmergencyContactEmployer").enable();
                        this.accountDetailsForm.get("isSecondaryEmergencyContact").enable();
                    }
                } else {
                    this.accountDetailsForm.get("secondaryEmergencyContactLastName").disable();
                    this.accountDetailsForm.get("secondaryEmergencyContactGenderTypeId").disable();
                    this.accountDetailsForm.get("secondaryEmergencyContactTypeId").disable();
                    this.accountDetailsForm.get("secondaryEmergencyContactBirthCountryId").disable();
                    this.accountDetailsForm.get("secondaryEmergencyContactAddress").disable();
                    this.accountDetailsForm.get("secondaryEmergencyContactCity").disable();
                    this.accountDetailsForm.get("secondaryEmergencyContactCountryId").disable();
                    this.accountDetailsForm.get("secondaryEmergencyContactPostCode").disable();
                    this.accountDetailsForm.get("secondaryEmergencyContactEmail").disable();
                    this.accountDetailsForm.get("secondaryEmergencyContactPhoneNo").disable();
                    this.accountDetailsForm.get("secondaryEmergencyContactEducationLevelId").disable();
                    this.accountDetailsForm.get("secondaryEmergencyContactOccupation").disable();
                    this.accountDetailsForm.get("secondaryEmergencyContactEmployer").disable();
                    this.accountDetailsForm.get("isSecondaryEmergencyContact").disable();
                }

                if (!firstName && this.allowEdit) {
                    this.accountDetailsForm.get("secondaryEmergencyContactLastName").reset();
                    this.accountDetailsForm.get("secondaryEmergencyContactGenderTypeId").reset();
                    this.accountDetailsForm.get("secondaryEmergencyContactTypeId").reset();
                    this.accountDetailsForm.get("secondaryEmergencyContactBirthCountryId").reset();
                    this.accountDetailsForm.get("secondaryEmergencyContactAddress").reset();
                    this.accountDetailsForm.get("secondaryEmergencyContactCity").reset();
                    this.accountDetailsForm.get("secondaryEmergencyContactPostCode").reset();
                    this.accountDetailsForm.get("secondaryEmergencyContactCountryId").reset();
                    this.accountDetailsForm.get("secondaryEmergencyContactEmail").reset();
                    this.accountDetailsForm.get("secondaryEmergencyContactPhoneNo").reset();
                    this.accountDetailsForm.get("secondaryEmergencyContactEducationLevelId").reset();
                    this.accountDetailsForm.get("secondaryEmergencyContactOccupation").reset();
                    this.accountDetailsForm.get("secondaryEmergencyContactEmployer").reset();
                    this.accountDetailsForm.get("isSecondaryEmergencyContact").reset();
                }
            });

        this.accountDetailsForm.get("identityDocumentNumber").valueChanges
            .subscribe(identityDocumentNumber => {
                if (identityDocumentNumber && identityDocumentNumber.trim().length === 0) {
                    this.accountDetailsForm.get("identityDocumentNumber").reset(null, { emitEvent: false });
                }

                if (identityDocumentNumber && this.allowEdit) {
                    this.accountDetailsForm.get("identityDocumentNumber").setValue(identityDocumentNumber.split(" ").join(""), { emitEvent: false });
                }

                this.checkIdDocInfo();

                //if (!identityDocumentNumber || !this.allowEdit) {
                //    //this.accountDetailsForm.get("identityDocumentTypeId").reset();
                //    //this.accountDetailsForm.get("identityDocumentTypeId").disable();
                //    //this.accountDetailsForm.get("identityDocumentCountryId").reset();
                //    //this.accountDetailsForm.get("identityDocumentCountryId").disable();
                //    //this.accountDetailsForm.get("identityDocumentNationalityId").reset();
                //    //this.accountDetailsForm.get("identityDocumentNationalityId").disable();
                //    //this.accountDetailsForm.get("identityDocumentBirthCommuneId").reset(null, { emitEvent: false });
                //    //this.accountDetailsForm.get("identityDocumentBirthCommuneId").disable();
                //    //this.accountDetailsForm.get("dateOfIssue").reset();
                //    //this.accountDetailsForm.get("dateOfIssue").disable();
                //    //this.accountDetailsForm.get("expiryDate").reset();
                //    //this.accountDetailsForm.get("expiryDate").disable();

                //    this.accountDetailsForm.get("identityDocumentNumber").setValidators(null);
                //    this.accountDetailsForm.get("identityDocumentTypeId").setValidators(null);
                //    this.accountDetailsForm.get("identityDocumentCountryId").setValidators(null);
                //    this.accountDetailsForm.get("identityDocumentNationalityId").setValidators(null);
                //    this.accountDetailsForm.get("identityDocumentBirthCommuneId").setValidators(null);
                //    this.accountDetailsForm.get("dateOfIssue").setValidators(null);
                //    this.accountDetailsForm.get("expiryDate").setValidators(null);

                //    this.identityDocumentInfoRequired = false;
                //} else {
                //    this.accountDetailsForm.get("identityDocumentNumber").setValue(identityDocumentNumber.split(" ").join(""), { emitEvent: false });
                //    this.accountDetailsForm.get("identityDocumentTypeId").setValidators([Validators.required]);
                //    this.accountDetailsForm.get("identityDocumentCountryId").setValidators([Validators.required]);
                //    this.accountDetailsForm.get("identityDocumentNationalityId").setValidators([Validators.required]);
                //    this.accountDetailsForm.get("dateOfIssue").setValidators([Validators.required]);
                //    this.accountDetailsForm.get("expiryDate").setValidators([Validators.required, dateGreaterThenValidator("dateOfIssue", true)]);

                //    //this.accountDetailsForm.get("identityDocumentTypeId").enable({ emitEvent: false });
                //    //this.accountDetailsForm.get("identityDocumentCountryId").enable({ emitEvent: false });
                //    //this.accountDetailsForm.get("identityDocumentNationalityId").enable({ emitEvent: false });
                //    //this.accountDetailsForm.get("dateOfIssue").enable({ emitEvent: false });
                //    //this.accountDetailsForm.get("expiryDate").enable({ emitEvent: false });

                //    if (this.accountDetailsForm.get("identityDocumentBirthCountryId").value === 11) {
                //        this.accountDetailsForm.get("identityDocumentBirthCommuneId").enable({ emitEvent: false });
                //        this.accountDetailsForm.get("identityDocumentBirthCommuneId").setValidators([Validators.required]);
                //    }

                //    this.identityDocumentInfoRequired = true;
                //}
            });

        this.accountDetailsForm.get("identityDocumentBirthCountryId").valueChanges
            .subscribe(selectedCountry => {
                if (selectedCountry === 11 && this.allowEdit /*&& this.accountDetailsForm.get("identityDocumentNumber").value*/) {
                    this.accountDetailsForm.get("identityDocumentBirthCommuneId").enable({ emitEvent: false });
                    this.accountDetailsForm.get("identityDocumentBirthCommuneId").setValidators([Validators.required]);
                    this.accountDetailsForm.get("identityDocumentBirthCommuneId").reset(null, { emitEvent: false });
                } else {
                    this.accountDetailsForm.get("identityDocumentBirthCommuneId").reset(null, { emitEvent: false });
                    this.accountDetailsForm.get("identityDocumentBirthCommuneId").disable({ emitEvent: false });
                }

                if (!selectedCountry && this.allowEdit) {
                    this.accountDetailsForm.get("identityDocumentBirthCountryId").enable({ emitEvent: false });
                    this.accountDetailsForm.get("identityDocumentBirthCountryId").setValidators([Validators.required]);
                }

                this.checkIdDocInfo();
            });

        this.accountDetailsForm.get("identityDocumentTypeId").valueChanges
            .subscribe((identityDocumentTypeId) => {
                //if we are changing to passport from identity doc and num images is greater then 1, then just take the first image
                if (identityDocumentTypeId === 2 && this.originalIdentityDocumentImages.length > 1) {
                    this.identityDocumentImages = _.cloneDeep(this.originalIdentityDocumentImages.filter(f => f.sequenceNumber === 0));

                    if (this.identityDocumentImages && this.identityDocumentImages.length > 0) {
                        if (!this.identityDocumentImages[0].needsStudentReview) {
                            this.identityDocumentRejectedReason = null;
                            this.needsAdminReviewForIdentityDocumentInformation = false;
                            this.identityDocumentImages[0].needsStudentReview = true;
                        }
                    }
                }

                if (identityDocumentTypeId === 1) {
                    this.identityDocumentImages = _.cloneDeep(this.originalIdentityDocumentImages);
                    this.identityDocumentRejectedReason = this.originalIdentityDocumentRejectedReason;
                    this.needsStudentReviewForIdentityDocumentInformation = !!this.identityDocumentImages.find(f => f.needsStudentReview);
                }

                this.checkIdDocInfo();
            });

        this.accountDetailsForm.get("identityDocumentBirthCommuneId").valueChanges
            .subscribe(identityDocumentBirthCommuneId => {
                this.checkIdDocInfo();
            });

        this.accountDetailsForm.get("identityDocumentNationalityId").valueChanges
            .subscribe(identityDocumentNationalityId => {
                this.checkIdDocInfo();
            });

        this.accountDetailsForm.get("dateOfIssue").valueChanges
            .subscribe(dateOfIssue => {
                this.checkIdDocInfo();
            });


        this.accountDetailsForm.get("expiryDate").valueChanges
            .subscribe(expiryDate => {
                this.checkIdDocInfo();
            });

        this.filteredItalianMunicipalities = this.accountDetailsForm.get("identityDocumentBirthCommuneId").valueChanges
            .pipe(
                debounceTime(200),
                (startWith<string | any>("")) as any,
                switchMap(val => {
                    return this.filterItalianMunicipality(val as string || "");
                })
            );
    }

    private filterItalianMunicipality(name: string): Observable<any[]> {
        if (name.length > 0) {
            const filterValue = name.toLowerCase();

            return (this.lookupsService.getItalianMunicipalities(filterValue)
                .pipe(
                    (map((response: any[]) => {
                        if (response && response.length > 0) {
                            this.italianMunicipalities = _.orderBy(response, "description");

                            return this.italianMunicipalities;
                        } else {
                            this.clearItalianMunicipality(true);
                        }
                    })) as any
                )) as any;
        }

        return new Observable<any[]>();
    }

    clearItalianMunicipality(resetAll: boolean) {
        if (resetAll) {
            this.accountDetailsForm.get("identityDocumentBirthCommuneId").setValue(null);
        }

        this.matAutocomplete.options.reset([]);
    }

    displayItalianMunicipality(italianMunicipalityId: number): string | undefined {
        if (!italianMunicipalityId) return undefined;

        const italianMunicipality = _.find(this.italianMunicipalities, { "id": italianMunicipalityId });

        if (italianMunicipality)
            return italianMunicipality.description;

        return undefined;
    }

    chooseFirstItalianMunicipality(): void {
        if (this.matAutocomplete.options.first && (this.matAutocomplete.options.first.value === this.accountDetailsForm.get("identityDocumentBirthCommuneId").value || typeof this.accountDetailsForm.get("identityDocumentBirthCommuneId").value === "string")) {
            this.matAutocomplete.options.first.select();

            this.accountDetailsForm.get("identityDocumentBirthCommuneId").setValue(this.matAutocomplete.options.first.value);
        }
    }

    checkIdDocInfo() {
        if (this.allowEdit) {
            this.identityDocumentInfoRequired = this.accountDetailsForm.get("identityDocumentNumber").value ||
                this.accountDetailsForm.get("identityDocumentTypeId").value ||
                this.accountDetailsForm.get("identityDocumentCountryId").value ||
                this.accountDetailsForm.get("identityDocumentNationalityId").value ||
                this.accountDetailsForm.get("identityDocumentBirthCommuneId").value ||
                this.accountDetailsForm.get("dateOfIssue").value ||
                this.accountDetailsForm.get("expiryDate").value ||
                (this.missingIdentityDocumentBirthCountry && this.accountDetailsForm.get("identityDocumentBirthCountryId").value);

            if (!this.identityDocumentInfoRequired) {
                this.accountDetailsForm.get("identityDocumentNumber").setValidators(null);
                this.accountDetailsForm.get("identityDocumentTypeId").setValidators(null);
                this.accountDetailsForm.get("identityDocumentCountryId").setValidators(null);
                this.accountDetailsForm.get("identityDocumentNationalityId").setValidators(null);
                this.accountDetailsForm.get("identityDocumentBirthCountryId").setValidators(null);
                this.accountDetailsForm.get("identityDocumentBirthCommuneId").setValidators(null);
                this.accountDetailsForm.get("dateOfIssue").setValidators(null);
                this.accountDetailsForm.get("expiryDate").setValidators(null);
            } else {
                this.accountDetailsForm.get("identityDocumentNumber").setValidators([Validators.required]);
                this.accountDetailsForm.get("identityDocumentTypeId").setValidators([Validators.required]);
                this.accountDetailsForm.get("identityDocumentCountryId").setValidators([Validators.required]);
                this.accountDetailsForm.get("identityDocumentNationalityId").setValidators([Validators.required]);
                this.accountDetailsForm.get("dateOfIssue").setValidators([Validators.required]);
                this.accountDetailsForm.get("expiryDate").setValidators([Validators.required, dateGreaterThenValidator("dateOfIssue", true)]);

                if (this.accountDetailsForm.get("identityDocumentBirthCountryId").value === 11) {
                    this.accountDetailsForm.get("identityDocumentBirthCommuneId").enable({ emitEvent: false });
                    this.accountDetailsForm.get("identityDocumentBirthCommuneId").setValidators([Validators.required]);
                }
            }

            setTimeout(() => {
                this.profileFormControls.identityDocumentBirthCommuneId.updateValueAndValidity({ emitEvent: false });
            }, null);
        }
    }

    updateImage() {
        const dialogConfig = new MatDialogConfig();

        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        dialogConfig.closeOnNavigation = true;
        dialogConfig.height = "auto";
        dialogConfig.width = "420px";
        dialogConfig.panelClass = "profileDialogContainer";

        dialogConfig.data = {
            imageSrc: this.imageSrc,
            isProfileImage: true,
            profileImageRejectedReason: this.profilePictureRejectedReason
        };

        const dialogRef = this.dialog.open(ProfileImageDialogComponent, dialogConfig);

        dialogRef.afterClosed()
            .subscribe(croppedImage => {
                if (croppedImage) {
                    this.imageSrc = croppedImage;
                    this.profilePictureChanged = true;
                    this.accountDetailsForm.markAsDirty();
                }
            });
    }

    onSelectedTabChange(event: MatTabChangeEvent) {
        this.selectedTabIndex = event.index;

        if (event.tab.textLabel === "Identity Document Information") {
            if (!this.identityDocumentDetailsId && this.allowEdit)
                this.viewIdentityDocumentHelp();

            if (this.currentUser.identityDocumentInfoExpired) {
                //this.accountDetailsForm.get("expiryDate").setErrors({ "expired": true });

                //setTimeout(() => {
                //    this.profileFormControls.expiryDate.updateValueAndValidity({ emitEvent: false });
                //});

                //remove the current images
                //his.identityDocumentImages = [];
            }
        }
    }

    get profileFormControls() { return this.accountDetailsForm.controls as any; }

    save(submitBtn: HTMLButtonElement) {
        if (this.accountDetailsForm.invalid || !this.customValidation()) {
            const result = [];
            Object.keys(this.accountDetailsForm.controls).forEach(key => {

                const controlErrors: ValidationErrors = this.accountDetailsForm.get(key).errors;
                if (controlErrors) {
                    Object.keys(controlErrors).forEach(keyError => {
                        result.push({
                            'control': key,
                            'error': keyError,
                            'value': controlErrors[keyError]
                        });
                    });
                }
            });

            if (result.find(f => f.control.indexOf("ssn") !== -1)) {
                //go to personal details tab
                if (this.selectedTabIndex !== 0) {
                    this.selectedTabIndex = 0;
                }
            } else {
                if (result.find(f => f.control.indexOf("EmergencyContact") !== -1)) {
                    //go to emergency contacts tab
                    if (this.selectedTabIndex !== 1) {
                        this.selectedTabIndex = 1;
                    }
                } else {
                    if (result.find(f => f.control.indexOf("EmergencyContact") === -1 && f.control.indexOf("ssn") === -1)) {
                        //go to identity documents tab
                        if (this.selectedTabIndex !== 2) {
                            this.selectedTabIndex = 2;
                        }
                    }
                }
            }

            return;
        }

        if (this.accountDetailsForm.dirty) {
            setTimeout(() => {
                submitBtn.disabled = true;
            }, 0);

            let studentIdentityDocumentData = null;

            if (this.profileFormControls.identityDocumentNumber.value) {
                studentIdentityDocumentData = {
                    id: this.identityDocumentDetailsId,
                    firstName: this.profileFormControls.identityDocumentFirstName.value.trim(),
                    lastName: this.profileFormControls.identityDocumentLastName.value.trim(),
                    middleName: !this.profileFormControls.identityDocumentMiddleName.value || !this.profileFormControls.identityDocumentMiddleName.value.trim() ? null : this.profileFormControls.identityDocumentMiddleName.value.trim(),
                    dateOfBirth: moment(this.profileFormControls.identityDocumentDateOfBirth.value).format("YYYY-MM-DD"),
                    genderTypeId: this.profileFormControls.identityDocumentGenderTypeId.value,
                    nationalityId: this.profileFormControls.identityDocumentNationalityId.value,
                    birthCountryId: this.profileFormControls.identityDocumentBirthCountryId.value,
                    birthCountryBbUniqueId: this.countries.find(f => f.id === this.profileFormControls.identityDocumentBirthCountryId.value).bbUniqueId,
                    issuingCountryId: this.profileFormControls.identityDocumentCountryId.value,
                    documentNumber: this.profileFormControls.identityDocumentNumber.value,
                    identityDocumentTypeId: this.profileFormControls.identityDocumentTypeId.value,
                    issueDate: moment(this.profileFormControls.dateOfIssue.value).format("YYYY-MM-DD"),
                    expirationDate: moment(this.profileFormControls.expiryDate.value).format("YYYY-MM-DD"),
                    birthMunicipalityId: this.profileFormControls.identityDocumentBirthCommuneId.value ? this.profileFormControls.identityDocumentBirthCommuneId.value : null,
                    rejectedReason: this.identityDocumentRejectedReason,
                    identityDocumentImages: this.identityDocumentImages,
                    isIdentityDocumentInfoUpdated: this.accountDetailsForm.get("identityDocumentNumber").dirty || this.accountDetailsForm.get("identityDocumentTypeId").dirty || this.accountDetailsForm.get("identityDocumentNationalityId").dirty || this.accountDetailsForm.get("identityDocumentCountryId").dirty || this.accountDetailsForm.get("identityDocumentBirthCommuneId").dirty || this.accountDetailsForm.get("dateOfIssue").dirty || this.accountDetailsForm.get("expiryDate").dirty || this.identityDocumentImagesChanged,
                    rowversion: this.identityDocumentDetailsRowversion,
                };
            }

            const studentEmergencyContacts = [];

            if (this.profileFormControls.primaryEmergencyContactFirstName.value) {
                const primaryEmergencyContact = {
                    id: this.primaryContactId,
                    firstName: this.profileFormControls.primaryEmergencyContactFirstName.value,
                    lastName: this.profileFormControls.primaryEmergencyContactLastName.value,
                    genderTypeId: this.profileFormControls.primaryEmergencyContactGenderTypeId.value,
                    studentEmergencyContactTypeId: this.profileFormControls.primaryEmergencyContactTypeId.value,
                    studentEmergencyContactTypeBbUniqueId: this.contactTypes.find(f => f.id === this.profileFormControls.primaryEmergencyContactTypeId.value).bbUniqueId,
                    birthCountryId: this.profileFormControls.primaryEmergencyContactBirthCountryId.value,
                    birthCountryBbUniqueId: this.profileFormControls.primaryEmergencyContactBirthCountryId.value ? this.countries.find(f => f.id === this.profileFormControls.primaryEmergencyContactBirthCountryId.value).bbUniqueId : null,
                    address: !this.profileFormControls.primaryEmergencyContactAddress.value || !this.profileFormControls.primaryEmergencyContactAddress.value.trim() ? null : this.profileFormControls.primaryEmergencyContactAddress.value.trim(),
                    city: !this.profileFormControls.primaryEmergencyContactCity.value || !this.profileFormControls.primaryEmergencyContactCity.value.trim() ? null : this.profileFormControls.primaryEmergencyContactCity.value.trim(),
                    stateId: this.profileFormControls.primaryEmergencyContactStateId.value,
                    stateDescription: this.profileFormControls.primaryEmergencyContactStateId.value ? this.states.find(f => f.id === this.profileFormControls.primaryEmergencyContactStateId.value).description : null,
                    stateCode: this.profileFormControls.primaryEmergencyContactStateId.value ? this.states.find(f => f.id === this.profileFormControls.primaryEmergencyContactStateId.value).code : null,
                    postCode: !this.profileFormControls.primaryEmergencyContactPostCode.value || !this.profileFormControls.primaryEmergencyContactPostCode.value.trim() ? null : this.profileFormControls.primaryEmergencyContactPostCode.value.trim(),
                    countryId: this.profileFormControls.primaryEmergencyContactCountryId.value,
                    countryBbUniqueId: this.profileFormControls.primaryEmergencyContactCountryId.value ? this.countries.find(f => f.id === this.profileFormControls.primaryEmergencyContactCountryId.value).bbUniqueId : null,
                    countryDescription: this.profileFormControls.primaryEmergencyContactCountryId.value ? this.countries.find(f => f.id === this.profileFormControls.primaryEmergencyContactCountryId.value).description : null,
                    phoneNo: this.profileFormControls.primaryEmergencyContactPhoneNo.value,
                    email: !this.profileFormControls.primaryEmergencyContactEmail.value || !this.profileFormControls.primaryEmergencyContactEmail.value.trim() ? null : this.profileFormControls.primaryEmergencyContactEmail.value.trim(),
                    educationLevelId: this.profileFormControls.primaryEmergencyContactEducationLevelId.value,
                    educationLevelBbUniqueId: this.profileFormControls.primaryEmergencyContactEducationLevelId.value ? this.educationLevels.find(f => f.id === this.profileFormControls.primaryEmergencyContactEducationLevelId.value).bbUniqueId : null,
                    occupation: !this.profileFormControls.primaryEmergencyContactOccupation.value || !this.profileFormControls.primaryEmergencyContactOccupation.value.trim() ? null : this.profileFormControls.primaryEmergencyContactOccupation.value.trim(),
                    employer: !this.profileFormControls.primaryEmergencyContactEmployer.value || !this.profileFormControls.primaryEmergencyContactEmployer.value.trim() ? null : this.profileFormControls.primaryEmergencyContactEmployer.value.trim(),
                    isEmergencyContact: this.profileFormControls.isPrimaryEmergencyContact.value ? this.profileFormControls.isPrimaryEmergencyContact.value : false,
                    isPrimaryContact: true,
                    bbUniqueId: this.primaryContactBbUniqueId,
                    rowversion: this.primaryContactRowversion
                }

                studentEmergencyContacts.push((primaryEmergencyContact) as any);
            }

            if (this.profileFormControls.secondaryEmergencyContactFirstName.value) {
                const secondaryEmergencyContact = {
                    id: this.secondaryContactId,
                    firstName: this.profileFormControls.secondaryEmergencyContactFirstName.value,
                    lastName: this.profileFormControls.secondaryEmergencyContactLastName.value,
                    genderTypeId: this.profileFormControls.secondaryEmergencyContactGenderTypeId.value,
                    studentEmergencyContactTypeId: this.profileFormControls.secondaryEmergencyContactTypeId.value,
                    studentEmergencyContactTypeBbUniqueId: this.contactTypes.find(f => f.id === this.profileFormControls.secondaryEmergencyContactTypeId.value).bbUniqueId,
                    birthCountryId: this.profileFormControls.secondaryEmergencyContactBirthCountryId.value,
                    birthCountryBbUniqueId: this.profileFormControls.secondaryEmergencyContactBirthCountryId.value ? this.countries.find(f => f.id === this.profileFormControls.secondaryEmergencyContactBirthCountryId.value).bbUniqueId : null,
                    address: !this.profileFormControls.secondaryEmergencyContactAddress.value || !this.profileFormControls.secondaryEmergencyContactAddress.value.trim() ? null : this.profileFormControls.secondaryEmergencyContactAddress.value.trim(),
                    city: !this.profileFormControls.secondaryEmergencyContactCity.value || !this.profileFormControls.secondaryEmergencyContactCity.value.trim() ? null : this.profileFormControls.secondaryEmergencyContactCity.value.trim(),
                    stateId: this.profileFormControls.secondaryEmergencyContactStateId.value,
                    stateDescription: this.profileFormControls.secondaryEmergencyContactStateId.value ? this.states.find(f => f.id === this.profileFormControls.secondaryEmergencyContactStateId.value).description : null,
                    stateCode: this.profileFormControls.primaryEmergencyContactStateId.value ? this.states.find(f => f.id === this.profileFormControls.primaryEmergencyContactStateId.value).code : null,
                    postCode: !this.profileFormControls.secondaryEmergencyContactPostCode.value || !this.profileFormControls.secondaryEmergencyContactPostCode.value.trim() ? null : this.profileFormControls.secondaryEmergencyContactPostCode.value.trim(),
                    countryId: this.profileFormControls.secondaryEmergencyContactCountryId.value,
                    countryBbUniqueId: this.profileFormControls.secondaryEmergencyContactCountryId.value ? this.countries.find(f => f.id === this.profileFormControls.secondaryEmergencyContactCountryId.value).bbUniqueId : null,
                    countryDescription: this.profileFormControls.secondaryEmergencyContactCountryId.value ? this.countries.find(f => f.id === this.profileFormControls.secondaryEmergencyContactCountryId.value).description : null,
                    phoneNo: this.profileFormControls.secondaryEmergencyContactPhoneNo.value,
                    email: !this.profileFormControls.secondaryEmergencyContactEmail.value || !this.profileFormControls.secondaryEmergencyContactEmail.value.trim() ? null : this.profileFormControls.secondaryEmergencyContactEmail.value.trim(),
                    educationLevelId: this.profileFormControls.secondaryEmergencyContactEducationLevelId.value,
                    educationLevelBbUniqueId: this.profileFormControls.secondaryEmergencyContactEducationLevelId.value ? this.educationLevels.find(f => f.id === this.profileFormControls.secondaryEmergencyContactEducationLevelId.value).bbUniqueId : null,
                    occupation: !this.profileFormControls.secondaryEmergencyContactOccupation.value || !this.profileFormControls.secondaryEmergencyContactOccupation.value.trim() ? null : this.profileFormControls.secondaryEmergencyContactOccupation.value.trim(),
                    employer: !this.profileFormControls.secondaryEmergencyContactEmployer.value || !this.profileFormControls.secondaryEmergencyContactEmployer.value.trim() ? null : this.profileFormControls.secondaryEmergencyContactEmployer.value.trim(),
                    isEmergencyContact: this.profileFormControls.isSecondaryEmergencyContact.value ? this.profileFormControls.isSecondaryEmergencyContact.value : false,
                    isSecondaryContact: false,
                    bbUniqueId: this.secondaryContactBbUniqueId,
                    rowversion: this.secondaryContactRowversion
                }

                studentEmergencyContacts.push((secondaryEmergencyContact) as any);
            }

            const data = {
                studentRegistrationId: this.id,
                ssn: this.ssnInput.value,
                imageSrc: this.imageSrc,
                rowversion: this.rowversion,
                studentEmergencyContacts: studentEmergencyContacts,
                studentIdentityDocumentData: studentIdentityDocumentData
            };

            this.userService.updateStudentDetails(data)
                .pipe(first())
                .subscribe(() => {
                    if (this.currentUser.profileImageNeedsStudentReview && this.currentUser.imageSrc && this.profilePictureChanged) {
                        _.remove(this.currentUser.userNotifications, f => f.code === "reUploadPicture");
                    }

                    this.currentUser.imageSrc = this.imageSrc;
                    this.currentUser.profileImageNeedsStudentReview = false;
                    this.currentUser.canApplyForHousing = (this.currentUser.imageSrc != null && ((this.currentUser.hasValidAdmissionsApplication && this.currentUser.studentCode != null) || this.currentUser.hasValidProgramCode));

                    //remove the picture notification
                    if (this.imageSrc) {
                        _.remove(this.currentUser.userNotifications, f => f.code === "picture");
                    }

                    //remove the emergency contacts notification
                    if (!this.currentUser.hasEmergencyContacts && data.studentEmergencyContacts?.length > 0) {
                        this.currentUser.hasEmergencyContacts = true;

                        _.remove(this.currentUser.userNotifications, f => f.code === "emergencyContacts");
                    }

                    //remove the identity document notification
                    if (!this.currentUser.hasIdentityDocumentInfo && data.studentIdentityDocumentData) {
                        this.currentUser.hasIdentityDocumentInfo = true;

                        _.remove(this.currentUser.userNotifications, f => f.code === "identityDocumentInfo");
                    }

                    this.currentUser.canViewOrientation = this.currentUser.canModifyOrientation && this.currentUser.imageSrc && this.currentUser.hasIdentityDocumentInfo && this.currentUser.hasEmergencyContacts;

                    //remove the identity document needs review notification
                    if (this.currentUser.identityDocumentInformationNeedsStudentReview && !data.studentIdentityDocumentData.identityDocumentImages.find(f => f.needsStudentReview)) {
                        this.currentUser.identityDocumentInformationNeedsStudentReview = false;

                        _.remove(this.currentUser.userNotifications, f => f.code === "identityDocumentInfoNeedsReview");
                    }

                    //if the permit to stay has been renewed, the id doc info is not expired, and the details have been updated
                    if (this.currentUser.identityDocumentInformationNeedsReview &&
                        (this.accountDetailsForm.get("identityDocumentNumber").dirty ||
                            this.accountDetailsForm.get("identityDocumentTypeId").dirty ||
                            this.accountDetailsForm.get("identityDocumentCountryId").dirty ||
                            this.accountDetailsForm.get("identityDocumentNationalityId").dirty ||
                            this.accountDetailsForm.get("identityDocumentBirthCommuneId").dirty ||
                            this.accountDetailsForm.get("dateOfIssue").dirty ||
                            this.accountDetailsForm.get("expiryDate").dirty ||
                            (this.missingIdentityDocumentBirthCountry && this.accountDetailsForm.get("identityDocumentBirthCountryId").dirty) ||
                            this.identityDocumentImagesChanged)) {

                        if (this.currentUser.identityDocumentInfoExpired) {
                            //remove the expired identity document notification if expiry date correctly updated
                            let expiryDate = new Date(data.studentIdentityDocumentData.expirationDate).setHours(0, 0, 0);
                            let today = new Date(); //.setHours(0,0,0);

                            let temp = new Date(today.setDate(today.getDate() + this.daysBeforePermitToStayExpiryForRenewal)).setHours(0, 0, 0);

                            if (expiryDate > temp) {
                                this.currentUser.identityDocumentInfoExpired = false;
                                this.currentUser.identityDocumentInformationNeedsReview = false;

                                if (this.currentUser.userNotifications.find(f => f.code === "identityDocumentInfoExpired"))
                                    _.remove(this.currentUser.userNotifications, f => f.code === "identityDocumentInfoExpired");
                            }
                        } else {
                            this.currentUser.identityDocumentInformationNeedsReview = false;

                            if (this.currentUser.userNotifications.find(f => f.code === "identityDocumentInfoExpired"))
                                _.remove(this.currentUser.userNotifications, f => f.code === "identityDocumentInfoExpired");
                        }
                    }

                    if (this.currentUser.canViewOrientation) {
                        if (!this.currentUser.userNotifications.find(f => f.code === "orientationSemesterStudent")) {
                            const tempStr = {
                                code: "orientationSemesterStudent",
                                message: "You have not yet <strong>Completed</strong> the <strong>Orientation</strong> Process.",
                                viewed: false,
                                url: "/orientation"
                            };

                            this.currentUser.userNotifications.push(tempStr);
                        }
                    }

                    this.storageService.store(this.appGlobals.localStorageKeys.currentUser, this.currentUser);

                    this.alertService.showMessage("Profile data saved successfully", "Profile", this.appGlobals.alertMessageTypes.success);

                    if (this.currentUser.canViewOrientation)
                        this.router.navigate(["/orientation"], { skipLocationChange: true });

                    this.dialogRef.close(data);
                });
        }
    }

    viewIdentityDocumentImages() {
        const dialogConfig = new MatDialogConfig();

        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        dialogConfig.closeOnNavigation = true;
        dialogConfig.height = "auto";
        dialogConfig.width = this.isMobile ? "75%" : "50%";
        dialogConfig.panelClass = "profileDialogContainer";

        if (this.identityDocumentImages.length === 0) {
            var newImage = {
                fileName: "tempImage",
                fileStream: null,
                fileType: null,
                id: 0,
                rowversion: null,
                sequenceNumber: 0
            };

            this.identityDocumentImages.push(newImage);
        }

        dialogConfig.data = {
            fullName: this.fullName,
            identityDocumentDetailsId: this.identityDocumentDetailsId,
            identityDocumentImages: this.identityDocumentImages,
            isPassport: this.profileFormControls.identityDocumentTypeId.value === 2,
            studentCode: this.studentCode,
            identityDocumentRejectedReason: this.identityDocumentRejectedReason,
            needsAdminReview: this.needsAdminReviewForIdentityDocumentInformation,
            identityDocumentInformationNeedsReview: this.currentUser.identityDocumentInformationNeedsReview,
            allowEdit: this.allowEdit
        };

        const dialogRef = this.dialog.open(IdentityDocumentImagesDialogComponent, dialogConfig);

        dialogRef.afterClosed().subscribe(data => {
            if (data) {
                if (data.newIdentityDocumentImages && data.newIdentityDocumentImages.length > 0) {
                    this.identityDocumentImages = data.newIdentityDocumentImages;
                    this.identityDocumentRejectedReason = data.identityDocumentRejectedReason;
                    this.needsStudentReviewForIdentityDocumentInformation = !!this.identityDocumentImages.find(f => f.needsStudentReview);
                    this.identityDocumentImagesChanged = true;

                    this.accountDetailsForm.markAsDirty();
                    this.accountDetailsForm.updateValueAndValidity();
                }
            }
        });
    }

    viewIdentityDocumentHelp() {
        const dialogConfig = new MatDialogConfig();

        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        dialogConfig.closeOnNavigation = true;
        dialogConfig.height = "auto";
        dialogConfig.width = this.isMobile ? "75%" : "50%";
        dialogConfig.panelClass = "profileDialogContainer";

        this.dialog.open(IdentityDocumentHelpDialogComponent, dialogConfig);
    }

    viewProfileImageHelp() {
        const dialogConfig = new MatDialogConfig();

        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        dialogConfig.closeOnNavigation = true;
        dialogConfig.height = "auto";
        dialogConfig.width = this.isMobile ? "75%" : "50%";
        dialogConfig.panelClass = "profileDialogContainer";

        const dialogRef = this.dialog.open(ProfileImageHelpDialogComponent, dialogConfig);

        dialogRef.afterClosed().subscribe(() => {
            this.updateImage();
        });
    }

    close() {
        if (this.accountDetailsForm.dirty) {
            //show unsaved changes dialog
            const dialogConfig = new MatDialogConfig();

            dialogConfig.disableClose = true;
            dialogConfig.autoFocus = true;
            dialogConfig.closeOnNavigation = true;
            dialogConfig.width = this.isMobile ? "60vw" : "30vw";
            dialogConfig.height = "auto";
            dialogConfig.panelClass = "unSavedChanges-dialog-container";

            const dialogRef = this.dialog.open(UnSavedChangesDialogComponent, dialogConfig);

            dialogRef.afterClosed().subscribe(result => {
                if (result === "confirm") {
                    this.dialogRef.close();
                }
            });
        } else {
            this.dialogRef.close();
        }
    }

    customValidation() {
        if (this.profileFormControls.identityDocumentNumber.value) {
            let expiryDate = new Date(this.profileFormControls.expiryDate.value).setHours(0, 0, 0);
            let today = new Date(); //.setHours(0,0,0);

            let temp = new Date(today.setDate(today.getDate() + this.daysBeforePermitToStayExpiryForRenewal)).setHours(0, 0, 0);

            if (this.currentUser.identityDocumentInfoExpired && (expiryDate < temp || !this.identityDocumentImagesChanged)) {
                this.alertService.showMessage("Your Identity Document is about to Expire. Please update accordingly including the images.", "Identity Document Information", this.appGlobals.alertMessageTypes.error);

                return false;
            } else {
                if (!this.identityDocumentImages || this.identityDocumentImages?.length === 0) {
                    this.alertService.showMessage("You need to Upload a copy (image) of your Identity Document", "Identity Document Information", this.appGlobals.alertMessageTypes.error);

                    return false;
                }
            }
        }

        return true;
    }
}